import BasemapsIcon from '@apps-common/ui-icons/Basemaps';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormGroup,
  Slide,
  Typography,
} from '@mui/material';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';

import * as intervals from '@explorer/util/intervals';
import FilterGroupPanel from '@explorer/components/ui/filter-group-panel';
import FilterTextField from '@explorer/components/ui/filter-textfield';
import FormControlLabel from '@explorer/components/ui/filter-form-control-label';
import LeftDrawerHeader from '../left-drawer-header.jsx';
import MosaicsList from './mosaics-list.jsx';
import MosaicsListV2 from './mosaics-list-v2/index.jsx';
import SeriesList from './series-list.jsx';
import {DRAWERS, LABS_MAP, TILE_MODE} from '@explorer/constants';
import {EmptyMessageContainer} from '@explorer/components/ui/message-container';
import {LeftDrawer} from '../drawers.jsx';
import {getSelectedSeries, getSeries} from '@explorer/selectors/series';
import {
  selectSeries,
  setActiveSeriesId,
  setIntervals,
} from '@explorer/actions/series';
import {setTileMode} from '@explorer/actions/tiles';
import {showLeftDrawer} from '@explorer/actions/ui';

const intervalValues = [
  intervals.WEEK,
  intervals.BIWEEK,
  intervals.MONTH,
  intervals.QUARTER,
];

const BasemapsSidebar = ({
  tileMode,
  setTileMode,
  open,
  series,
  intervalsFilter,
  setIntervals,
  ...props
}) => {
  const {t} = useTranslation();
  const [nameFilter, setNameFilter] = useState('');
  const filteredSeries = useMemo(() => {
    const filtered = series?.filter(s => {
      return (
        intervalsFilter[s.interval] &&
        s.name.toLowerCase().includes(nameFilter.toLowerCase())
      );
    });
    filtered?.sort(
      (a, b) =>
        intervalValues.indexOf(a.interval) - intervalValues.indexOf(b.interval)
    );
    return filtered;
  }, [series, intervalsFilter, nameFilter]);

  useEffect(() => {
    if (open) {
      if (tileMode !== TILE_MODE.BASEMAPS) {
        setTileMode(TILE_MODE.BASEMAPS);
      }
    }
  }, [open, setTileMode, tileMode]);

  const handleIntervalChange = event => {
    const {name, checked} = event.target;

    setIntervals({
      [name]: checked,
    });
  };

  const handleBackClick = () => {
    props.setActiveSeriesId(null);
  };

  return (
    <LeftDrawer open={open} data-qe="basemap-sidebar">
      <LeftDrawerHeader
        title={t('sidebarsBasemap.title')}
        showBackButton={Boolean(props.activeSeriesId)}
        onBackClick={handleBackClick}
        subHeader={props.activeSeriesId ? props.selectedSeries?.name : null}
        icon={<BasemapsIcon sx={{color: theme => theme.palette.grey[500]}} />}
      />
      <Box overflow="hidden" height="100%">
        <Slide
          in={Boolean(props.activeSeriesId)}
          direction="right"
          timeout={{enter: 200}}
        >
          <Box
            height={props.activeSeriesId ? '100%' : 0}
            display="flex"
            flexDirection="column"
          >
            {props.useListv2 ? (
              <MosaicsListV2 activeSeriesId={props.activeSeriesId} />
            ) : (
              <MosaicsList />
            )}
          </Box>
        </Slide>
        <Slide
          in={!props.activeSeriesId}
          direction="left"
          timeout={{enter: 200}}
        >
          <Box
            height={!props.activeSeriesId ? '100%' : 0}
            display="flex"
            flexDirection="column"
          >
            <Box
              px={2}
              py={1}
              sx={{backgroundColor: theme => theme.palette.grey[100]}}
            >
              <FilterTextField
                placeholder={t('sidebarsBasemap.placeholderNameFilter')}
                fullWidth
                value={nameFilter}
                onChange={setNameFilter}
                data-qe="filter-text-field"
                sx={{backgroundColor: theme => theme.palette.background.paper}}
              />
              <Box marginTop={2}>
                <FilterGroupPanel
                  title={t('sidebarsBasemap.toggleSectionIntervals')}
                  sx={{backgroundColor: theme => theme.palette.grey[200]}}
                  defaultExpanded={false}
                  data-qe="filter-menu-dropdown"
                >
                  <FormGroup row>
                    {intervalValues.map(interval => (
                      <FormControlLabel
                        key={interval}
                        checked={intervalsFilter[interval]}
                        control={
                          <Checkbox
                            checked={intervalsFilter[interval]}
                            onChange={handleIntervalChange}
                            name={interval}
                            data-qe={`${interval}-checkbox`}
                          />
                        }
                        label={t(`basemapLabels.${intervals.labels[interval]}`)}
                      />
                    ))}
                  </FormGroup>
                </FilterGroupPanel>
              </Box>
            </Box>

            <Divider sx={{marginBottom: 1}} />

            {filteredSeries?.length ? (
              <SeriesList series={filteredSeries} />
            ) : (
              <EmptyMessageContainer data-qe="error-message">
                <Typography variant="h6" paragraph>
                  {t('sidebarsBasemap.noResults')}
                </Typography>
                <Typography variant="body1">
                  {t('sidebarsBasemap.noResultsSuggestion')}
                </Typography>
              </EmptyMessageContainer>
            )}
          </Box>
        </Slide>
      </Box>
    </LeftDrawer>
  );
};

BasemapsSidebar.propTypes = {
  activeSeriesId: PropTypes.string,
  intervalsFilter: PropTypes.object,
  open: PropTypes.bool,
  selectSeries: PropTypes.func,
  selectedSeries: PropTypes.object,
  series: PropTypes.array,
  setActiveSeriesId: PropTypes.func,
  setIntervals: PropTypes.func,
  setTileMode: PropTypes.func,
  showLeftDrawer: PropTypes.func,
  tileMode: PropTypes.string,
  useListv2: PropTypes.bool,
};

const mapStateToProps = state => ({
  activeSeriesId: state.series.activeId,
  intervalsFilter: state.series.intervalsFilter,
  open: state.ui.drawers.left === DRAWERS.BASEMAPS,
  selectedSeries: getSelectedSeries(state),
  series: getSeries(state),
  seriesLoading: state.series.loading,
  tileMode: state.tiles.mode,
  useListv2: !state.labs[LABS_MAP.SINGLE_ACTION],
});

const mapDispatchProps = {
  selectSeries,
  setActiveSeriesId,
  setIntervals,
  setTileMode,
  showLeftDrawer,
};

export default connect(mapStateToProps, mapDispatchProps)(BasemapsSidebar);
